/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { ensureAuthenticated } from '../../utilities/is-authenticated';

/**
 * GetPriceInfo Input Action
 */
export class GetPriceInfoInput implements Msdyn365.IActionInput {
    public priceInfo: IGetPriceInfoDetailData;
    constructor(priceInfo: IGetPriceInfoDetailData) {
        this.priceInfo = priceInfo;
    }

    public getCacheKey = () => `GetPriceInfo-${JSON.stringify(this.priceInfo)}`;
    public getCacheObjectType = () => 'GetPriceInfo';
    public dataCacheType = (): Msdyn365.CacheType => 'application';
}

export interface IGetPriceInfoDetailData {
    ProductNumber: string;
    SalesOrg: string;
    SoldToId: string;
    ShipToId: string;
    DistributionChannel: string;
    Division: string;
    Incoterm: string;
    EndCustomer: any;
    RequestedDate: any;
    Language: string;
}

export interface IPriceInfoResponseData {
    '@odata.context': string;
    GroupScale: boolean;
    PriceAmount: string;
    PriceCurrency: string;
    PriceUnit: string;
    PriceFactor: string;
    PaymentTerm: string;
    ScaleUnit: string;
    Scales: IGetPriceInfoDetailScales[];
    ExtensionProperties: any[];
    MissingParameters?: string;
    PriceMissing?: string;
}

export interface IGetPriceInfoDetailScales {
    Bound: string;
    PriceAmount: string;
    ExtensionProperties: any[];
}

const createInput = (args: Msdyn365.ICreateActionContext): Msdyn365.IActionInput => {
    return new GetPriceInfoInput({} as IGetPriceInfoDetailData);
};

async function action(input: GetPriceInfoInput, ctx: Msdyn365.IActionContext): Promise<IPriceInfoResponseData> {
    ensureAuthenticated(ctx);
    let priceInfoData: IPriceInfoResponseData = {} as IPriceInfoResponseData;
    await Msdyn365.sendRequest(
        `${ctx.requestContext.apiSettings.baseUrl}Commerce/GetPriceInfo`,
        'post',
        {
            priceInfo: input.priceInfo
        },
        {
            headers: {
                OUN: ctx.requestContext.apiSettings.oun,
                Authorization: `id_token ${ctx.requestContext.user.token}`
            }
        }
    )
        .then(res => {
            priceInfoData = res.data as IPriceInfoResponseData;
        })
        .catch(error => {
            console.error('Error saving cart', error);
            throw new Error('Error saving cart');
        });
    return priceInfoData;
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IPriceInfoResponseData>>action,
    id: 'GetPriceInfo',
    input: createInput
    // isBatched: true
});
